'use client';

import { useEffect } from 'react';

import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import { Button } from '@/shared-ui';

const AUTO_DISMISS_MS = 5000;

interface Props {
  isVisible: boolean;
  onClose: () => void;
}

export default function ProjectUsabilityTranscriptErrorPrompt({
  isVisible,
  onClose,
}: Props) {
  const t = useTranslations('Usability');

  useEffect(() => {
    if (!isVisible) return;
    const id = window.setTimeout(onClose, AUTO_DISMISS_MS);
    return () => window.clearTimeout(id);
  }, [isVisible, onClose]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className='absolute bottom-24 left-6 z-50 flex max-w-95 items-center gap-6 rounded-2xl bg-neutral-950 px-6 py-4 shadow-lg'
      data-test='usability-transcript-error-prompt'
      role='alert'
      aria-live='assertive'
    >
      <FontAwesomeIcon
        icon={faCircleExclamation}
        className='shrink-0 text-white'
        aria-hidden
        size='xl'
      />
      <div className='flex min-w-0 flex-1 flex-col gap-1'>
        <span className='text-sm font-semibold text-white'>
          {t('transcriptErrorTitle')}
        </span>
        <span className='text-xs font-normal text-white'>
          {t('transcriptErrorBody')}
        </span>
      </div>
      <Button
        variant='ghost'
        size='sm'
        onClick={onClose}
        data-test='usability-transcript-error-prompt-close'
        className='shrink-0 text-white hover:enabled:bg-gray-700'
        aria-label={t('closeAria')}
      >
        <FontAwesomeIcon icon={faClose} />
      </Button>
    </div>
  );
}
